'use client';

import React, { useContext } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { OpenFeatureSupportedFlags } from '@mentimeter/flags/openfeature/types';
import { useDevelopmentFlags } from '../hooks/useDevelopmentFlags';
import { DevelopmentToolsContext } from '../context';
import { useConfidenceFlags } from '../hooks/useConfidenceFlags';
import { CopyButton } from './components/Copy';
import { ImportButton } from './components/Import';
import { Bubble } from './components/Bubble';
import { Notice } from './components/Notice';
import { Search } from './components/Search';
import { Modal } from './components/Modal';
import { FlagList, FlagSection } from './components/FlagSection';
import { Flag } from './components/Flag';
import { QuickMaskBubble } from './components/QuickMaskBubble';
import { usePathRevalidator } from './use-path-revalidator';

export function DevelopmentToolsModal({
  confidenceExperiments = {},
}: {
  confidenceExperiments?: OpenFeatureSupportedFlags;
}) {
  const developmentToolsContext = useContext(DevelopmentToolsContext);
  const [searchString, setSearchString] = React.useState('');
  const revalidateCurrentPath = usePathRevalidator();

  const {
    activeConfidenceFlags,
    confidenceFlags,
    resetAllConfidenceFlags,
    resetConfidenceOverride,
    setConfidenceOverride,
  } = useConfidenceFlags({
    flags: confidenceExperiments,
    searchString,
  });

  const {
    developmentFlags,
    activeDevelopmentFlags,
    setDevelopmentFlag,
    resetDevelopmentFlag,
    resetAllDevelopmentFlags,
  } = useDevelopmentFlags({ searchString });

  const alwaysShowDevelopmentToolsFlag = activeDevelopmentFlags.find(
    (x) => x.key === 'always-show-development-tools',
  );
  const alwaysShowBubble = alwaysShowDevelopmentToolsFlag?.value === 'yes';

  const activeFlagsCount =
    activeConfidenceFlags.concat(activeDevelopmentFlags).length -
    (alwaysShowDevelopmentToolsFlag ? 1 : 0); // don't count the always show development tools flag

  const flagHits = developmentFlags.concat(confidenceFlags).length;

  const i18nMaskingToggle = activeDevelopmentFlags.find(
    (x) => x.active && x.key === 'quick-mask-translated-messages-toggle',
  );
  const i18nMasking = activeDevelopmentFlags.find(
    (x) => x.key === 'mask-translated-messages',
  );

  const enableMaskingToggle =
    i18nMaskingToggle && i18nMaskingToggle.value !== 'off';
  const enableI18nMasking = i18nMasking?.value === 'on';

  const hasFlags = alwaysShowBubble || activeFlagsCount > 0;

  return (
    <>
      {hasFlags && (
        <Bubble
          onClick={developmentToolsContext.openModal}
          overriddenExperimentsCount={activeFlagsCount}
        />
      )}
      {activeFlagsCount > 0 && enableMaskingToggle && (
        <QuickMaskBubble
          onToggle={handleI18nMaskingToggle}
          mode={i18nMaskingToggle.value === 'toggle' ? 'toggle' : 'hover'}
          enabled={enableI18nMasking}
        />
      )}

      <Modal
        open={developmentToolsContext.isModalOpen}
        onClose={() => {
          developmentToolsContext.closeModal();
        }}
      >
        <Box display="grid" gridAutoRows="auto" gap={3}>
          <Notice />

          <Search
            value={searchString}
            onSearch={(searchString) => setSearchString(searchString)}
            onClear={() => setSearchString('')}
          />

          <Box
            mb="space3"
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
            gap={2}
          >
            <ImportButton
              clearAll={resetAllConfidenceFlags}
              flags={[...developmentFlags, ...confidenceFlags]}
              onImportSuccess={(flags) => {
                setSearchString('');

                flags.forEach((flag) => {
                  if (!flag.value) return;

                  if (flag.type === 'development') {
                    setDevelopmentFlag(flag.key, flag.value);
                  } else if (flag.type === 'confidence') {
                    setConfidenceOverride(flag.key, flag.value);
                  }
                });

                refreshFlags();
              }}
            />

            <CopyButton
              disabled={activeFlagsCount < 1}
              activeFlags={[
                ...activeConfidenceFlags,
                ...activeDevelopmentFlags,
              ]}
            />

            <Button
              size="compact"
              variant="outline"
              onClick={() => {
                resetAllDevelopmentFlags();
                resetAllConfidenceFlags();
                refreshFlags();
              }}
              disabled={activeFlagsCount < 1}
            >
              Reset all flags
            </Button>
          </Box>

          {flagHits === 0 && <Text color="textWeaker">No results</Text>}

          {hasFlags && (
            <FlagSection id="enabled" title={`Enabled (${activeFlagsCount})`}>
              <FlagList>
                {activeConfidenceFlags.map((flag) => (
                  <Flag
                    key={flag.key}
                    flag={flag}
                    onChange={(value) => {
                      setConfidenceOverride(flag.key, value);
                      refreshFlags();
                    }}
                    onReset={() => {
                      resetConfidenceOverride(flag.key);
                      refreshFlags();
                    }}
                  />
                ))}

                {activeDevelopmentFlags.map((flag) => (
                  <Flag
                    key={flag.key}
                    flag={flag}
                    onChange={(value) => {
                      setDevelopmentFlag(flag.key, value);
                      refreshFlags();
                    }}
                    onReset={() => {
                      resetDevelopmentFlag(flag.key);
                      refreshFlags();
                    }}
                  />
                ))}
              </FlagList>
            </FlagSection>
          )}

          {confidenceFlags.length > 0 && (
            <FlagSection
              id="open-feature-flags"
              title="Experimental flags"
              variant="confidence"
            >
              <FlagList>
                {confidenceFlags.map((flag) => (
                  <Flag
                    key={flag.key}
                    flag={flag}
                    onChange={(value) => {
                      setConfidenceOverride(flag.key, value);
                      refreshFlags();
                    }}
                    onReset={() => {
                      resetConfidenceOverride(flag.key);
                      refreshFlags();
                    }}
                  />
                ))}
              </FlagList>
            </FlagSection>
          )}

          {developmentFlags.length > 0 && (
            <FlagSection
              id="development-flags"
              title="Development flags"
              variant="development"
            >
              <FlagList>
                {developmentFlags.map((flag) => (
                  <Flag
                    key={flag.key}
                    flag={flag}
                    onChange={(value) => {
                      setDevelopmentFlag(flag.key, value);
                      refreshFlags();
                    }}
                    onReset={() => {
                      resetDevelopmentFlag(flag.key);
                      refreshFlags();
                    }}
                  />
                ))}
              </FlagList>
            </FlagSection>
          )}
        </Box>
      </Modal>
    </>
  );

  function refreshFlags() {
    // Refresh the app to hydrate server-side experiments.
    if (globalThis.__mentimeterRuntime === 'app') {
      revalidateCurrentPath();
    }
  }

  function handleI18nMaskingToggle(show: boolean) {
    setDevelopmentFlag('mask-translated-messages', show ? 'on' : 'off');
    refreshFlags();
  }
}
